import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../hooks/useAuth.js'
import { useReveal } from '../hooks/useReveal.js'
import LanguagePicker from '../components/LanguagePicker.js'

/* ── Language data ──────────────────────────────────────────────────────── */

const LANGUAGES_ROW1 = [
  { code: 'en', flag: '\u{1F1FA}\u{1F1F8}' },
  { code: 'ja', flag: '\u{1F1EF}\u{1F1F5}' },
  { code: 'zh', flag: '\u{1F1E8}\u{1F1F3}' },
  { code: 'de', flag: '\u{1F1E9}\u{1F1EA}' },
  { code: 'hi', flag: '\u{1F1EE}\u{1F1F3}' },
  { code: 'fr', flag: '\u{1F1EB}\u{1F1F7}' },
  { code: 'ko', flag: '\u{1F1F0}\u{1F1F7}' },
  { code: 'pt', flag: '\u{1F1E7}\u{1F1F7}' },
  { code: 'it', flag: '\u{1F1EE}\u{1F1F9}' },
  { code: 'es', flag: '\u{1F1EA}\u{1F1F8}' },
  { code: 'id', flag: '\u{1F1EE}\u{1F1E9}' },
  { code: 'nl', flag: '\u{1F1F3}\u{1F1F1}' },
  { code: 'tr', flag: '\u{1F1F9}\u{1F1F7}' },
  { code: 'tl', flag: '\u{1F1F5}\u{1F1ED}' },
  { code: 'pl', flag: '\u{1F1F5}\u{1F1F1}' },
  { code: 'sv', flag: '\u{1F1F8}\u{1F1EA}' },
  { code: 'mn', flag: '\u{1F1F2}\u{1F1F3}' },
]
const LANGUAGES_ROW2 = [
  { code: 'bg', flag: '\u{1F1E7}\u{1F1EC}' },
  { code: 'ro', flag: '\u{1F1F7}\u{1F1F4}' },
  { code: 'ar', flag: '\u{1F1F8}\u{1F1E6}' },
  { code: 'cs', flag: '\u{1F1E8}\u{1F1FF}' },
  { code: 'el', flag: '\u{1F1EC}\u{1F1F7}' },
  { code: 'fi', flag: '\u{1F1EB}\u{1F1EE}' },
  { code: 'hr', flag: '\u{1F1ED}\u{1F1F7}' },
  { code: 'ms', flag: '\u{1F1F2}\u{1F1FE}' },
  { code: 'sk', flag: '\u{1F1F8}\u{1F1F0}' },
  { code: 'da', flag: '\u{1F1E9}\u{1F1F0}' },
  { code: 'ta', flag: '\u{1F1F1}\u{1F1F0}' },
  { code: 'uk', flag: '\u{1F1FA}\u{1F1E6}' },
  { code: 'ru', flag: '\u{1F1F7}\u{1F1FA}' },
  { code: 'hu', flag: '\u{1F1ED}\u{1F1FA}' },
  { code: 'no', flag: '\u{1F1F3}\u{1F1F4}' },
  { code: 'vi', flag: '\u{1F1FB}\u{1F1F3}' },
]

/* ── Gallery card data ─────────────────────────────────────────────────── */

const GALLERY_CARDS = [
  { big: true, img: '/images/gallery-1.webp', langCode: 'ja', flag: '\u{1F1EF}\u{1F1F5}', title: "Why Blue Whales Don't Get Cancer \u2014 Peto's Paradox", channel: 'Kurzgesagt \u2013 In a Nutshell \u00b7 14:22', channelShort: 'Kurzgesagt \u2013 In a Nutshell', dot: '#5B7FFF', status: 'done' as const },
  { img: '/images/gallery-2.webp', langCode: 'es', flag: '\u{1F1EA}\u{1F1F8}', title: 'The Most Useful Math Fact', channel: 'Veritasium', channelShort: 'Veritasium', dot: '#AA44FF', status: 'done' as const },
  { img: '/images/gallery-3.webp', langCode: 'ko', flag: '\u{1F1F0}\u{1F1F7}', title: 'The Psychology of Self-Motivation', channel: 'TEDx Talks', channelShort: 'TEDx Talks', dot: '#00BB77', status: 'proc' as const },
  { img: '/images/gallery-4.webp', langCode: 'de', flag: '\u{1F1E9}\u{1F1EA}', title: 'I Built a Theme Park With a 99.9% Death Rate', channel: 'MrBeast Gaming', channelShort: 'MrBeast Gaming', dot: '#FF6622', status: 'done' as const },
  { img: '/images/gallery-5.webp', langCode: 'hi', flag: '\u{1F1EE}\u{1F1F3}', title: 'Happiness Is Always a Choice', channel: 'Kurzgesagt', channelShort: 'Kurzgesagt', dot: '#22CC55', status: 'done' as const },
]

/* ── Comparison data (moved inside component for i18n) ──────────────── */

const COMP_HEADERS = ['SilkVoice', 'Rask AI', 'HeyGen', 'Veed.io'] as const

/* ── Proof strip data ─────────────────────────────────────────────────── */

const PROOF_ITEMS = [
  { prefix: '', num: '33', suffix: '' },
  { prefix: '', num: '85', suffix: '%' },
  { prefix: '', num: '2', suffix: '\u00d7' },
  { prefix: '$', num: '0', suffix: '' },
]

/* ── HeroCard thumbnail data ──────────────────────────────────────────── */

const HERO_THUMBS = [
  { img: '/images/gallery-1.webp', channel: 'Kurzgesagt', title: "Why Blue Whales Don't Get Cancer", lang: '\u{1F1EF}\u{1F1F5} JP' },
  { img: '/images/gallery-2.webp', channel: 'Veritasium', title: 'The Most Useful Math Fact', lang: '\u{1F1EA}\u{1F1F8} ES' },
  { img: '/images/gallery-3.webp', channel: 'MrBeast', title: 'I Built a Theme Park With a 99.9% Death Rate', lang: '\u{1F1E9}\u{1F1EA} DE' },
  { img: '/images/gallery-4.webp', channel: 'TEDx Talks', title: 'The Psychology of Self-Motivation', lang: '\u{1F1F0}\u{1F1F7} KO' },
]

/* ── Pricing data (moved inside component for i18n) ─────────────────── */

const BUNDLES = [
  { ext: 'Plus', studio: 'Basic', price: '24', savings: '4' },
  { ext: 'Pro', studio: 'Pro', price: '59', savings: '9' },
]

/* ── Step icons & data ────────────────────────────────────────────────── */

const STEP_ICONS = [
  /* 01 link */ <><path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71" /><path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71" /></>,
  /* 02 monitor */ <><rect x="2" y="3" width="20" height="14" rx="2" /><path d="M8 21h8M12 17v4" /></>,
  /* 03 users */ <><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" /></>,
  /* 04 play */ <><polygon points="8 5 19 12 8 19" /></>,
]

/* ── Main component ───────────────────────────────────────────────────── */

export default function LandingPage() {
  const { t }    = useTranslation()
  const { user } = useAuth()
  const ctaTo    = user ? '/dashboard' : '/auth'
  const nav      = useNavigate()
  const [pricingTab, setPricingTab] = useState<'extension' | 'studio'>('extension')

  useReveal()

  /* ── i18n-aware data ──────────────────────────────────────────────── */

  const compRows = [
    { label: t('landing.compare.rowPriceMin'), cells: [
      { text: '$0.30\u2013$0.63', cls: 'sv win' },
      { text: '$1.20\u2013$2.00' },
      { text: '~$2.20' },
      { text: '$2.75' },
    ]},
    { label: t('landing.compare.rowFreeTier'), cells: [
      { text: t('landing.compare.fiveMinTrial'), cls: 'win' },
      { text: t('landing.compare.none'), cls: 'no' },
      { text: t('landing.compare.none'), cls: 'no' },
      { text: t('landing.compare.none'), cls: 'no' },
    ]},
    { label: t('landing.compare.rowLanguages'), cells: [
      { text: '33', cls: 'sv' },
      { text: '130+' },
      { text: '40+' },
      { text: '25+' },
    ]},
    { label: t('landing.compare.rowGenderVoices'), cells: [
      { text: t('landing.compare.yes'), cls: 'win' },
      { text: t('landing.compare.partial') },
      { text: t('landing.compare.yes') },
      { text: t('landing.compare.no'), cls: 'no' },
    ]},
    { label: t('landing.compare.rowSpeakerDetect'), cells: [
      { text: t('landing.compare.yes'), cls: 'win' },
      { text: t('landing.compare.yes') },
      { text: t('landing.compare.yes') },
      { text: t('landing.compare.no'), cls: 'no' },
    ]},
    { label: t('landing.compare.rowTenMinCost'), cells: [
      { text: '$3\u2013$6', cls: 'sv win' },
      { text: '$20' },
      { text: '$22' },
      { text: '$28' },
    ]},
  ]

  const extensionTiers = [
    { name: t('landing.tiers.tierFree'), price: '0', minutes: t('landing.tiers.fiveMinTrial') },
    { name: t('landing.tiers.tierPlus'), price: '9', minutes: t('landing.tiers.minPerMo', { count: 300 }) },
    { name: t('landing.tiers.tierPro'), price: '19', minutes: t('landing.tiers.minPerMo', { count: 1000 }) },
  ]

  const studioTiers = [
    { name: t('landing.tiers.tierFree'), price: '0', minutes: t('landing.tiers.fiveMinTrial') },
    { name: t('landing.tiers.tierBasic'), price: '19', minutes: t('landing.tiers.minPerMo', { count: 30 }) },
    { name: t('landing.tiers.tierPro'), price: '49', minutes: t('landing.tiers.minPerMo', { count: 120 }) },
  ]

  return (
    <div style={{ overflow: 'hidden' }}>

      {/* ── Hero ──────────────────────────────────────────────────── */}
      <div className="hero-section" style={{ padding: '120px 40px 80px', maxWidth: 1040, margin: '0 auto', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 72, alignItems: 'center' }}>
        <div>
          {/* Eyebrow pill */}
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            padding: '4px 12px', borderRadius: 20,
            background: 'rgba(255,90,60,0.06)', border: '1px solid rgba(255,90,60,0.18)',
            fontSize: 11, fontWeight: 700, color: 'var(--color-accent)',
            textTransform: 'uppercase', letterSpacing: '0.09em',
            marginBottom: 20,
          }}>
            <span className="eyebrow-dot" style={{
              width: 5, height: 5, borderRadius: '50%',
              background: 'linear-gradient(135deg, #FF006E, #FFBE0B)',
            }} />
            {t('landing.eyebrow')}
          </div>

          <h1 style={{ fontSize: 'clamp(38px, 5vw, 62px)', fontWeight: 800, lineHeight: 1.05, letterSpacing: '-0.03em', marginBottom: 18 }}>
            {t('landing.hero.line1')}<br />
            <em className="serif">{t('landing.hero.line2')}</em>
          </h1>

          <p style={{ fontSize: 17, color: 'var(--color-text-muted)', lineHeight: 1.7, maxWidth: 480, marginBottom: 10 }}>
            {t('landing.hero.subtitle')}
          </p>
          <p style={{ fontSize: 14, color: 'var(--color-muted-ink)', lineHeight: 1.6, maxWidth: 480, marginBottom: 28 }}>
            {t('landing.hero.extensionPunch')}
          </p>

          {/* CTAs */}
          <div className="hero-ctas" style={{ display: 'flex', gap: 10, marginBottom: 28, flexWrap: 'wrap' }}>
            <Link to={ctaTo} className="landing-btn landing-btn-fill">
              <svg viewBox="0 0 24 24"><polygon points="8 5 19 12 8 19" fill="white" stroke="none" /></svg>
              {t('landing.hero.cta1')}
            </Link>
            <Link to={ctaTo} className="landing-btn landing-btn-line">
              <svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" /><path d="M2 12h20M12 2a15.3 15.3 0 010 20" /></svg>
              {t('landing.hero.cta2')}
            </Link>
          </div>

          {/* Trust items */}
          <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap' }}>
            {(['trust1', 'trust2', 'trust3'] as const).map((key) => (
              <div key={key} style={{ fontSize: 12, color: 'var(--color-muted-ink)', display: 'flex', alignItems: 'center', gap: 5 }}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="var(--color-accent)" strokeWidth="2.5">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
                {t(`landing.trust.${key}`)}
              </div>
            ))}
          </div>
        </div>

        {/* Hero card — hidden on mobile via CSS */}
        <div className="hero-card-wrap" style={{ animation: 'fadeIn .65s .18s ease both' }}>
          <HeroCard />
        </div>
      </div>

      {/* ── Proof strip ───────────────────────────────────────────── */}
      <div className="proof reveal" style={{ borderTop: '1px solid var(--color-glass-border)', borderBottom: '1px solid var(--color-glass-border)' }}>
        <div className="proof-inner" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', maxWidth: 1040, margin: '0 auto', padding: '0 40px' }}>
          {PROOF_ITEMS.map((item, i) => (
            <div key={i} className="proof-item" style={{
              padding: '36px 24px', textAlign: 'center',
              borderLeft: i > 0 ? '1px solid var(--color-glass-border)' : 'none',
            }}>
              <div style={{ fontSize: 38, fontWeight: 800, letterSpacing: '-0.04em', lineHeight: 1, marginBottom: 4 }}>
                {item.prefix}
                <span style={{ background: 'linear-gradient(135deg,#FF006E,#FFBE0B)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
                  {item.num}
                </span>
                {item.suffix}
              </div>
              <div style={{ fontSize: 12, color: 'var(--color-muted-ink)' }}>
                {t(`landing.proof.${(['languages', 'cheaper', 'faster', 'start'] as const)[i]}`)}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── How It Works ──────────────────────────────────────────── */}
      <section id="how" style={{ padding: '96px 0' }}>
        <div style={{ maxWidth: 1040, margin: '0 auto', padding: '0 40px' }}>
          <div className="reveal">
            <span className="label" style={{ marginBottom: 10 }}>{t('landing.how.label')}</span>
            <h2 style={{ fontSize: 'clamp(28px, 3.5vw, 44px)', fontWeight: 800, lineHeight: 1.08, letterSpacing: '-0.025em' }}>
              {t('landing.how.title1', 'Four steps. Two minutes.')}<br />
              <em className="serif">{t('landing.how.title2', 'Zero downloads.')}</em>
            </h2>
          </div>

          <div className="steps reveal" style={{
            display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 1,
            marginTop: 56, background: 'var(--color-glass-border)',
            borderRadius: 'var(--radius-lg)', overflow: 'hidden',
            transitionDelay: '0.1s',
          }}>
            {([1, 2, 3, 4] as const).map((step) => (
              <div key={step} className="step-card" style={{
                background: 'var(--color-surface)', padding: '28px 24px',
                transition: 'background 0.2s',
              }}>
                <div style={{ fontSize: 11, fontWeight: 800, color: 'var(--color-faint)', letterSpacing: '0.09em', textTransform: 'uppercase', marginBottom: 20 }}>
                  {String(step).padStart(2, '0')}
                </div>
                <div style={{
                  width: 34, height: 34, borderRadius: 8,
                  border: '1px solid var(--color-glass-border)',
                  background: 'rgba(255,255,255,0.05)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  marginBottom: 14,
                }}>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="var(--color-text-muted)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    {STEP_ICONS[step - 1]}
                  </svg>
                </div>
                <h3 style={{ fontSize: 14, fontWeight: 700, marginBottom: 7 }}>{t(`landing.how.step${step}.title`)}</h3>
                <p style={{ fontSize: 12, color: 'var(--color-text-muted)', lineHeight: 1.7 }}>{t(`landing.how.step${step}.caption`)}</p>
                {step === 2 && (
                  <div style={{
                    marginTop: 10, padding: '8px 10px', borderRadius: 6,
                    background: 'rgba(255,190,11,0.04)', border: '1px solid rgba(255,190,11,0.1)',
                    fontSize: 11, color: 'rgba(220,170,40,0.75)', lineHeight: 1.5,
                  }}>
                    {t('landing.how.step2.note', '\u26a1 Keep the tab open during capture. You can browse normally otherwise.')}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      <hr style={{ border: 'none', borderTop: '1px solid var(--color-glass-border)' }} />

      {/* ── Video Gallery ─────────────────────────────────────────── */}
      <section style={{ padding: '96px 0' }}>
        <div style={{ maxWidth: 1040, margin: '0 auto', padding: '0 40px' }}>
          <div className="reveal">
            <span className="label" style={{ marginBottom: 10 }}>{t('landing.gallery.label')}</span>
            <h2 style={{ fontSize: 'clamp(28px, 3.5vw, 44px)', fontWeight: 800, lineHeight: 1.08, letterSpacing: '-0.025em' }}>
              {t('landing.gallery.title1', 'Found a great video in')}<br />
              {t('landing.gallery.title2pre', 'the ')}<em className="serif">{t('landing.gallery.title2', 'wrong language?')}</em>
            </h2>
            <p style={{ fontSize: 17, color: 'var(--color-text-muted)', lineHeight: 1.7, maxWidth: 480, marginTop: 12 }}>
              {t('landing.gallery.subtitle', 'Works with any public YouTube video \u2014 cooking, tech, documentaries, lectures, podcasts. Paste. Pick. Done.')}
            </p>
          </div>

          <div className="gallery-grid reveal" style={{
            display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10,
            marginTop: 52, transitionDelay: '0.1s',
          }}>
            {GALLERY_CARDS.map((card, i) => (
              <div key={i} className={`gcard${card.big ? ' big' : ''}`} onClick={() => nav(ctaTo)} style={{
                background: 'var(--color-surface)',
                border: '1px solid var(--color-glass-border)',
                borderRadius: 'var(--radius-lg)',
                overflow: 'hidden',
                cursor: 'pointer',
                transition: 'box-shadow 0.22s, transform 0.22s, border-color 0.22s',
                ...(card.big ? { gridColumn: '1 / 3' } : {}),
              }}>
                {/* Thumbnail */}
                <div style={{ position: 'relative', overflow: 'hidden' }}>
                  <div style={{
                    width: '100%', height: '100%', position: 'relative',
                    aspectRatio: card.big ? '16/8' : '16/9',
                  }}>
                    <img src={card.img} alt={card.title} loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                  </div>
                  {/* Overlay */}
                  <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(transparent 40%, rgba(0,0,0,0.75) 100%)' }} />
                  {/* Play button */}
                  <div className="gthumb-play" style={{
                    position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)',
                    width: 40, height: 40, borderRadius: '50%',
                    background: 'rgba(255,255,255,0.12)', backdropFilter: 'blur(8px)',
                    border: '1px solid rgba(255,255,255,0.18)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    opacity: 0, transition: 'opacity 0.2s, transform 0.2s',
                  }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="#fff" stroke="none">
                      <polygon points="8 5 19 12 8 19" />
                    </svg>
                  </div>
                  {/* Info overlay */}
                  <div style={{ position: 'absolute', bottom: 10, left: 12, right: 12 }}>
                    <div style={{
                      display: 'inline-flex', alignItems: 'center', gap: 4,
                      padding: '2px 7px', borderRadius: 4,
                      background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(6px)',
                      fontSize: 9, fontWeight: 700, color: 'rgba(255,255,255,0.7)',
                      border: '1px solid rgba(255,255,255,0.1)', marginBottom: 4,
                    }}>
                      {card.flag} {t('landing.gallery.dubLabel', { lang: t(`languages.${card.langCode}`) })}
                    </div>
                    <div style={{ fontSize: card.big ? 14 : 11, fontWeight: 600, color: 'rgba(255,255,255,0.9)', lineHeight: 1.35 }}>
                      {card.title}
                    </div>
                    <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', marginTop: 2 }}>
                      {card.channel}
                    </div>
                  </div>
                </div>
                {/* Meta bar */}
                <div style={{
                  padding: '12px 14px', display: 'flex', alignItems: 'center', gap: 8,
                  borderTop: '1px solid var(--color-glass-border)',
                }}>
                  <div style={{ width: 6, height: 6, borderRadius: '50%', background: card.dot, flexShrink: 0 }} />
                  <span style={{ fontSize: 11, color: 'var(--color-muted-ink)', fontWeight: 600 }}>{card.channelShort}</span>
                  <div style={{
                    marginLeft: 'auto', fontSize: 10, fontWeight: 700,
                    display: 'flex', alignItems: 'center', gap: 4,
                    color: card.status === 'done' ? '#00A870' : 'var(--color-accent)',
                  }}>
                    <span style={{
                      width: 5, height: 5, borderRadius: '50%', background: 'currentColor',
                      ...(card.status === 'proc' ? { animation: 'blinkDot 1.4s ease-in-out infinite' } : {}),
                    }} />
                    {card.status === 'done' ? t('landing.gallery.statusDone') : t('landing.gallery.statusProc')}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <hr style={{ border: 'none', borderTop: '1px solid var(--color-glass-border)' }} />

      {/* ── Extension ───────────────────────────────────────────── */}
      <section id="extension" style={{ padding: '96px 0' }}>
        <div style={{ maxWidth: 1040, margin: '0 auto', padding: '0 40px' }}>
          <div className="reveal">
            <span className="label" style={{ marginBottom: 10 }}>{t('landing.extension.label')} <span className="coming-soon-badge" style={{ marginLeft: 6, fontSize: 10, padding: '3px 8px' }}>{t('comingSoon')}</span></span>
            <h2 style={{ fontSize: 'clamp(28px, 3.5vw, 44px)', fontWeight: 800, lineHeight: 1.08, letterSpacing: '-0.025em' }}>
              {t('landing.extension.title1')}<br />
              <em className="serif">{t('landing.extension.title2')}</em>
            </h2>
            <p style={{ fontSize: 17, color: 'var(--color-text-muted)', lineHeight: 1.7, maxWidth: 480, marginTop: 12 }}>
              {t('landing.extension.subtitle')}
            </p>
          </div>

          <div className="ext-grid reveal" style={{
            display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 28,
            marginTop: 52, alignItems: 'start', transitionDelay: '0.1s',
          }}>
            {/* Extension browser mockup */}
            <div className="ext-mockup" style={{
              background: 'var(--color-surface)',
              border: '1px solid var(--color-glass-border)',
              borderRadius: 'var(--radius-lg)',
              boxShadow: 'var(--shadow-lg)',
              overflow: 'hidden', position: 'relative',
            }}>
              {/* Gradient top border */}
              <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, background: 'linear-gradient(135deg,#FFBE0B,#FF006E,#00F5D4,#3A0CA3)', opacity: 0.7, zIndex: 1 }} />
              {/* Browser chrome */}
              <div style={{
                display: 'flex', alignItems: 'center', gap: 8, padding: '11px 14px',
                background: 'rgba(255,255,255,0.03)', borderBottom: '1px solid var(--color-glass-border)',
              }}>
                <div style={{ display: 'flex', gap: 5 }}>
                  <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#FF5F57' }} />
                  <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#FEBC2E' }} />
                  <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#28C840' }} />
                </div>
                <div style={{
                  flex: 1, maxWidth: 260, margin: '0 auto', height: 20,
                  background: 'rgba(255,255,255,0.06)', border: '1px solid var(--color-glass-border)',
                  borderRadius: 5, display: 'flex', alignItems: 'center', padding: '0 8px', gap: 5,
                }}>
                  <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#4CAF50', flexShrink: 0 }} />
                  <span style={{ fontSize: 10, color: 'var(--color-muted-ink)', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>youtube.com/watch?v=dQw4w9W...</span>
                </div>
              </div>
              {/* Video area */}
              <div style={{ position: 'relative', aspectRatio: '16/9' }}>
                <img src="/images/ext-video.webp" alt="" loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                {/* Play button center */}
                <div style={{
                  position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)',
                  width: 48, height: 48, borderRadius: '50%',
                  background: 'rgba(255,255,255,0.1)', backdropFilter: 'blur(8px)',
                  border: '1px solid rgba(255,255,255,0.15)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="rgba(255,255,255,0.8)" stroke="none">
                    <polygon points="8 5 19 12 8 19" />
                  </svg>
                </div>
                {/* Progress bar */}
                <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 3, background: 'rgba(255,255,255,0.1)' }}>
                  <div style={{ width: '35%', height: '100%', background: '#FF0000', borderRadius: '0 2px 2px 0' }} />
                </div>
                {/* Extension popup overlay */}
                <div style={{
                  position: 'absolute', top: 12, right: 12,
                  width: 180, padding: 12,
                  background: 'rgba(20,20,30,0.92)', backdropFilter: 'blur(12px)',
                  borderRadius: 10, border: '1px solid rgba(255,255,255,0.12)',
                  boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
                    <SilkVoiceLogo size={14} id="ext" />
                    <span style={{ fontSize: 10, fontWeight: 700, color: 'rgba(255,255,255,0.9)' }}>SilkVoice</span>
                  </div>
                  <div style={{ fontSize: 9, fontWeight: 600, color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>
                    {t('landing.extMockup.dubbingTo', { lang: t('languages.ko') })}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
                    <div style={{ flex: 1, height: 3, borderRadius: 2, background: 'rgba(255,255,255,0.1)', overflow: 'hidden' }}>
                      <div style={{ width: '65%', height: '100%', background: 'linear-gradient(90deg, #FF006E, #FFBE0B)', borderRadius: 2 }} />
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      <div style={{ width: 5, height: 5, borderRadius: '50%', background: '#00A870', animation: 'blinkDot 1.4s ease-in-out infinite' }} />
                      <span style={{ fontSize: 9, color: '#00A870', fontWeight: 600 }}>{t('landing.extMockup.live')}</span>
                    </div>
                    <div style={{ marginLeft: 'auto', display: 'flex', gap: 4 }}>
                      <div style={{
                        width: 22, height: 22, borderRadius: 5,
                        background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.1)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
                      }}>
                        <svg width="8" height="8" viewBox="0 0 24 24" fill="rgba(255,255,255,0.7)" stroke="none">
                          <rect x="6" y="4" width="4" height="16" /><rect x="14" y="4" width="4" height="16" />
                        </svg>
                      </div>
                      <div style={{
                        width: 22, height: 22, borderRadius: 5,
                        background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.1)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
                      }}>
                        <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.7)" strokeWidth="2.5">
                          <path d="M11 5L6 9H2v6h4l5 4V5z" /><path d="M19.07 4.93a10 10 0 010 14.14M15.54 8.46a5 5 0 010 7.07" />
                        </svg>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Feature cards */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {([1, 2, 3, 4] as const).map((n) => (
                <div key={n} style={{
                  padding: '20px 22px', background: 'var(--color-surface)',
                  border: '1px solid var(--color-glass-border)',
                  borderRadius: 'var(--radius-lg)',
                  transition: 'box-shadow 0.2s',
                }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
                    <div style={{
                      width: 34, height: 34, borderRadius: 8, flexShrink: 0,
                      background: 'rgba(255,255,255,0.05)', border: '1px solid var(--color-glass-border)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="var(--color-text-muted)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                        {n === 1 && <><polygon points="8 5 19 12 8 19" /><line x1="5" y1="3" x2="5" y2="21" /></>}
                        {n === 2 && <><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" /></>}
                        {n === 3 && <><rect x="2" y="3" width="20" height="14" rx="2" /><path d="M8 21h8M12 17v4" /></>}
                        {n === 4 && <><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /></>}
                      </svg>
                    </div>
                    <div>
                      <h3 style={{ fontSize: 14, fontWeight: 700, marginBottom: 4 }}>{t(`landing.extension.feat${n}.title`)}</h3>
                      <p style={{ fontSize: 13, color: 'var(--color-text-muted)', lineHeight: 1.6 }}>{t(`landing.extension.feat${n}.desc`)}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <hr style={{ border: 'none', borderTop: '1px solid var(--color-glass-border)' }} />

      {/* ── Pricing ───────────────────────────────────────────────── */}
      <section id="pricing" style={{ padding: '96px 0' }}>
        <div style={{ maxWidth: 1040, margin: '0 auto', padding: '0 40px' }}>
          <div className="reveal" style={{ maxWidth: 520 }}>
            <span className="label" style={{ marginBottom: 10 }}>{t('landing.tiers.label')}</span>
            <h2 style={{ fontSize: 'clamp(28px, 3.5vw, 44px)', fontWeight: 800, lineHeight: 1.08, letterSpacing: '-0.025em' }}>
              {t('landing.tiers.title1')}<br />
              <em className="serif">{t('landing.tiers.title2')}</em>
            </h2>
            <p style={{ fontSize: 14, color: 'var(--color-muted-ink)', lineHeight: 1.7, marginTop: 10 }}>
              {t('landing.tiers.subtitle')}
            </p>
          </div>

          {/* Toggle */}
          <div className="reveal" style={{ display: 'flex', justifyContent: 'center', marginTop: 36 }}>
            <div className="pricing-toggle">
              <button
                className={`pricing-toggle-btn${pricingTab === 'extension' ? ' active' : ''}`}
                onClick={() => setPricingTab('extension')}
              >
                {t('landing.tiers.extension.title')} <span className="coming-soon-badge" style={{ marginLeft: 4, fontSize: 8, padding: '2px 5px' }}>{t('comingSoon')}</span>
              </button>
              <button
                className={`pricing-toggle-btn${pricingTab === 'studio' ? ' active' : ''}`}
                onClick={() => setPricingTab('studio')}
              >
                {t('landing.tiers.studio.title')}
              </button>
            </div>
          </div>

          {/* Single centered card */}
          <div className="pcard reveal" style={{
            maxWidth: 600, margin: '32px auto 0',
            background: 'var(--color-surface)',
            border: '1px solid var(--color-glass-border)',
            borderRadius: 'var(--radius-lg)',
            padding: 28, position: 'relative',
            transition: 'box-shadow 0.22s, transform 0.22s',
          }}>
            {/* Card header */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
              <div style={{
                width: 28, height: 28, borderRadius: 7,
                background: 'rgba(255,255,255,0.05)', border: '1px solid var(--color-glass-border)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                {pricingTab === 'extension' ? (
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="var(--color-text-muted)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10" /><path d="M2 12h20M12 2a15.3 15.3 0 010 20" />
                  </svg>
                ) : (
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="var(--color-text-muted)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <polygon points="8 5 19 12 8 19" />
                  </svg>
                )}
              </div>
              <span style={{ fontSize: 16, fontWeight: 700 }}>
                {pricingTab === 'extension' ? t('landing.tiers.extension.title') : t('landing.tiers.studio.title')}
              </span>
            </div>
            <p style={{ fontSize: 13, color: 'var(--color-muted-ink)', marginBottom: 20 }}>
              {pricingTab === 'extension' ? t('landing.tiers.extension.subtitle') : t('landing.tiers.studio.subtitle')}
            </p>

            {/* Tier rows */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
              {(pricingTab === 'extension' ? extensionTiers : studioTiers).map((tier, i) => (
                <div key={tier.price} style={{
                  display: 'grid', gridTemplateColumns: '72px 1fr auto', alignItems: 'baseline', gap: 8,
                  padding: '14px 0',
                  borderTop: i > 0 ? '1px solid var(--color-glass-border)' : 'none',
                }}>
                  <span style={{ fontSize: 13, fontWeight: 700 }}>{tier.name}</span>
                  <span style={{ fontSize: 13, color: 'var(--color-text-muted)' }}>{tier.minutes}</span>
                  <span style={{ fontSize: tier.price === '0' ? 14 : 22, fontWeight: 800, letterSpacing: '-0.03em' }}>
                    {tier.price === '0' ? (
                      <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--color-muted-ink)' }}>{t('landing.tiers.free')}</span>
                    ) : (
                      <>${tier.price}<span style={{ fontSize: 13, fontWeight: 500, color: 'var(--color-muted-ink)' }}>{t('landing.tiers.perMonth')}</span></>
                    )}
                  </span>
                </div>
              ))}
            </div>

            {/* CTA */}
            {pricingTab === 'extension' ? (
              <button disabled className="pcard-btn accent" style={{
                display: 'block', width: '100%', padding: 10, borderRadius: 8,
                fontSize: 13, fontWeight: 700, cursor: 'not-allowed',
                transition: 'all 0.18s', marginTop: 18, textAlign: 'center',
                background: 'linear-gradient(135deg,#FF006E,#FFBE0B)', border: '1.5px solid transparent', color: '#fff',
                opacity: 0.5,
              }}>
                {t('comingSoon')}
              </button>
            ) : (
              <Link to={ctaTo} className="pcard-btn accent" style={{
                display: 'block', width: '100%', padding: 10, borderRadius: 8,
                fontSize: 13, fontWeight: 700, cursor: 'pointer',
                transition: 'all 0.18s', marginTop: 18, textAlign: 'center',
                textDecoration: 'none',
                background: 'linear-gradient(135deg,#FF006E,#FFBE0B)', border: '1.5px solid transparent', color: '#fff',
                boxShadow: '0 1px 3px rgba(255,0,110,.18), 0 4px 14px rgba(255,0,110,.14)',
              }}>
                {t('landing.tiers.startFree')}
              </Link>
            )}

            {/* Bundle add-on */}
            <div style={{
              marginTop: 22, padding: '16px 18px',
              border: '1px solid var(--color-glass-border)',
              borderRadius: 10,
              background: 'rgba(255,255,255,0.02)',
              opacity: 0.55, pointerEvents: 'none',
            }}>
              <span style={{ fontSize: 13, fontWeight: 700, marginBottom: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
                {pricingTab === 'extension' ? t('landing.tiers.addStudio') : t('landing.tiers.addExtension')}
                <span className="coming-soon-badge" style={{ fontSize: 9, padding: '2px 6px' }}>{t('comingSoon')}</span>
              </span>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {BUNDLES.map((b) => (
                  <div key={b.price} style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
                  }}>
                    <span style={{ fontSize: 13, fontWeight: 600 }}>
                      {t('landing.tiers.bundleLabel', { ext: t('landing.tiers.tier' + b.ext), studio: t('landing.tiers.tier' + b.studio) })}
                    </span>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
                      <span style={{ fontSize: 14, fontWeight: 700 }}>{t('landing.tiers.bundlePrice', { price: b.price })}</span>
                      <span style={{
                        fontSize: 11, fontWeight: 700,
                        background: 'linear-gradient(135deg,#FF006E,#FFBE0B)',
                        WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text',
                      }}>
                        {t('landing.tiers.bundle.save', { amount: b.savings })}
                      </span>
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <p className="reveal" style={{ textAlign: 'center', marginTop: 18, fontSize: 12, color: 'var(--color-muted-ink)' }}>
            {t('landing.tiers.topupNote')}{' '}
            <strong style={{ color: 'var(--color-text-muted)' }}>$0.30/min</strong>
            {' \u2014 '}{t('landing.tiers.topupNever')}{' '}
            <Link to="/pricing" style={{ color: 'var(--color-accent)', textDecoration: 'none', fontWeight: 600 }}>
              {t('landing.tiers.allPlans')} &rarr;
            </Link>
          </p>
        </div>
      </section>

      <hr style={{ border: 'none', borderTop: '1px solid var(--color-glass-border)' }} />

      {/* ── Comparison Table ──────────────────────────────────────── */}
      <section id="compare" style={{ padding: '96px 0' }}>
        <div style={{ maxWidth: 1040, margin: '0 auto', padding: '0 40px' }}>
          <div className="reveal">
            <span className="label" style={{ marginBottom: 10 }}>{t('landing.compare.label', 'vs competitors')}</span>
            <h2 style={{ fontSize: 'clamp(28px, 3.5vw, 44px)', fontWeight: 800, lineHeight: 1.08, letterSpacing: '-0.025em' }}>
              {t('landing.compare.title1', 'Save up to ')}<em className="serif">{t('landing.compare.title2', '85%')}</em>
            </h2>
            <p style={{ fontSize: 17, color: 'var(--color-text-muted)', lineHeight: 1.7, maxWidth: 480, marginTop: 10 }}>
              {t('landing.compare.subtitle')}
            </p>
          </div>

          <div className="reveal" style={{
            marginTop: 52, overflowX: 'auto',
            borderRadius: 'var(--radius-lg)', border: '1px solid var(--color-glass-border)',
            background: 'var(--color-surface)', boxShadow: 'var(--shadow-sm)',
            transitionDelay: '0.1s',
          }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr>
                  <th style={{ padding: '14px 18px', textAlign: 'left', fontWeight: 700, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.07em', color: 'var(--color-muted-ink)', borderBottom: '1px solid var(--color-glass-border)', background: 'rgba(255,255,255,0.025)' }}>
                    {t('landing.compare.feature')}
                  </th>
                  {COMP_HEADERS.map((h, i) => (
                    <th key={i} style={{
                      padding: '14px 18px', textAlign: 'left', fontWeight: 700, fontSize: 11,
                      textTransform: 'uppercase', letterSpacing: '0.07em',
                      borderBottom: '1px solid var(--color-glass-border)',
                      color: i === 0 ? 'var(--color-text)' : 'var(--color-muted-ink)',
                      background: i === 0 ? 'rgba(255,90,60,0.04)' : 'rgba(255,255,255,0.025)',
                    }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {compRows.map((row, ri) => (
                  <tr key={ri}>
                    <td style={{
                      padding: '12px 18px',
                      borderBottom: ri < compRows.length - 1 ? '1px solid var(--color-glass-border)' : 'none',
                      color: 'var(--color-text-muted)',
                      background: 'rgba(255,90,60,0.025)',
                      borderLeft: '2px solid rgba(255,90,60,0.25)',
                    }}>
                      {row.label}
                    </td>
                    {row.cells.map((cell, ci) => {
                      const isWin = cell.cls?.includes('win')
                      const isNo = cell.cls?.includes('no')
                      const isSv = ci === 0
                      return (
                        <td key={ci} style={{
                          padding: '12px 18px',
                          borderBottom: ri < compRows.length - 1 ? '1px solid var(--color-glass-border)' : 'none',
                          background: isSv ? 'rgba(255,90,60,0.025)' : undefined,
                          color: isWin ? '#00A870' : isNo ? 'var(--color-faint)' : isSv ? 'var(--color-text)' : 'var(--color-text-muted)',
                          fontWeight: isWin ? 700 : isSv ? 600 : 400,
                        }}>
                          {cell.text}
                        </td>
                      )
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <hr style={{ border: 'none', borderTop: '1px solid var(--color-glass-border)' }} />

      {/* ── Compliance ────────────────────────────────────────────── */}
      <section style={{ padding: '96px 0' }}>
        <div style={{ maxWidth: 1040, margin: '0 auto', padding: '0 40px' }}>
          <div className="reveal">
            <span className="label" style={{ marginBottom: 10 }}>{t('landing.compliance.label', 'Privacy & compliance')}</span>
            <h2 style={{ fontSize: 'clamp(28px, 3.5vw, 44px)', fontWeight: 800, lineHeight: 1.08, letterSpacing: '-0.025em' }}>
              {t('landing.compliance.title1', 'Your browser. Your audio.')}<br />
              <em className="serif">{t('landing.compliance.title2', 'We never touch YouTube.')}</em>
            </h2>
          </div>

          <div className="compliance-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginTop: 48 }}>
            {([
              { key: 'badge1', icon: <><rect x="2" y="3" width="20" height="14" rx="2" /><path d="M8 21h8M12 17v4" /></>, delay: '0s' },
              { key: 'badge2', icon: <><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /></>, delay: '0.1s' },
              { key: 'badge3', icon: <><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></>, delay: '0.2s' },
            ] as const).map(({ key, icon, delay }) => (
              <div key={key} className="cgcard reveal" style={{
                padding: 24, background: 'var(--color-surface)',
                border: '1px solid var(--color-glass-border)',
                borderRadius: 'var(--radius-lg)',
                transition: 'box-shadow 0.2s',
                transitionDelay: delay,
              }}>
                <div style={{
                  width: 34, height: 34, borderRadius: 8,
                  background: 'rgba(255,255,255,0.05)', border: '1px solid var(--color-glass-border)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  marginBottom: 14,
                }}>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="var(--color-text-muted)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    {icon}
                  </svg>
                </div>
                <h3 style={{ fontSize: 17, fontWeight: 700, marginBottom: 6 }}>{t(`landing.compliance.${key}`)}</h3>
                <p style={{ fontSize: 14, color: 'var(--color-text-muted)', lineHeight: 1.7 }}>{t(`landing.compliance.${key}Desc`)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <hr style={{ border: 'none', borderTop: '1px solid var(--color-glass-border)' }} />

      {/* ── Languages Marquee ─────────────────────────────────────── */}
      <div id="langs" style={{ padding: '64px 0', borderTop: '1px solid var(--color-glass-border)' }}>
        <div style={{ maxWidth: 1040, margin: '0 auto', padding: '0 40px' }} className="reveal">
          <span className="label" style={{ marginBottom: 10 }}>{t('landing.languages.label', 'Languages')}</span>
          <h2 style={{ fontSize: 'clamp(28px, 3.5vw, 44px)', fontWeight: 800, lineHeight: 1.08, letterSpacing: '-0.025em' }}>
            {t('landing.languages.title1', '33 languages. ')}<em className="serif">{t('landing.languages.title2', 'One click.')}</em>
          </h2>
        </div>

        <div style={{ marginTop: 32, display: 'flex', flexDirection: 'column', gap: 8 }}>
          <LanguageMarquee row={LANGUAGES_ROW1} speed={30} reverse={false} t={t} />
          <LanguageMarquee row={LANGUAGES_ROW2} speed={26} reverse={true} t={t} />
        </div>
      </div>

      {/* ── Final CTA ─────────────────────────────────────────────── */}
      <div className="cta-section" style={{ padding: '80px 40px', maxWidth: 1040, margin: '0 auto' }}>
        <div className="cta-inner reveal" style={{
          background: 'var(--color-surface)',
          border: '1px solid var(--color-glass-border)',
          borderRadius: 20, padding: 64,
          textAlign: 'center', position: 'relative', overflow: 'hidden',
          boxShadow: 'var(--shadow-lg)',
        }}>
          {/* Silk gradient top border */}
          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, background: 'linear-gradient(135deg,#FFBE0B,#FF006E,#00F5D4,#3A0CA3)', opacity: 0.6 }} />
          {/* Radial glow */}
          <div style={{ position: 'absolute', top: -60, left: '50%', transform: 'translateX(-50%)', width: 400, height: 200, borderRadius: '50%', background: 'radial-gradient(ellipse,rgba(255,0,110,0.05),transparent 65%)', pointerEvents: 'none' }} />

          <h2 style={{ fontSize: 'clamp(28px, 3.5vw, 44px)', fontWeight: 800, lineHeight: 1.08, letterSpacing: '-0.025em', marginBottom: 12 }}>
            {t('landing.finalCta.title1', 'Ready to hear the world')}<br />
            {t('landing.finalCta.title2pre', 'in ')}<em className="serif">{t('landing.finalCta.title2', 'your language?')}</em>
          </h2>
          <p style={{ fontSize: 17, color: 'var(--color-text-muted)', lineHeight: 1.7, maxWidth: 480, margin: '12px auto 28px', textAlign: 'center' }}>
            {t('landing.finalCta.sub')}
          </p>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, flexWrap: 'wrap' }}>
            <Link to={ctaTo} className="landing-btn landing-btn-fill">
              <svg viewBox="0 0 24 24"><polygon points="8 5 19 12 8 19" fill="white" stroke="none" /></svg>
              {t('landing.hero.cta1')}
            </Link>
            <Link to={ctaTo} className="landing-btn landing-btn-line">
              <svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" /><path d="M2 12h20M12 2a15.3 15.3 0 010 20" /></svg>
              {t('landing.hero.cta2')}
            </Link>
          </div>
          <p style={{ marginTop: 18, fontSize: 12, color: 'var(--color-muted-ink)' }}>
            {t('landing.finalCta.fine', 'No credit card required \u00b7 5 free minutes \u00b7 From $0.30/min')}
          </p>
        </div>
      </div>

      {/* ── Footer ────────────────────────────────────────────────── */}
      <footer style={{ borderTop: '1px solid var(--color-glass-border)', padding: '30px 0' }}>
        <div className="footer-inner" style={{
          maxWidth: 1040, margin: '0 auto', padding: '0 40px',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          flexWrap: 'wrap', gap: 14,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 17, fontWeight: 400, fontFamily: 'var(--font-serif)', fontStyle: 'italic' }}>
            <SilkVoiceLogo size={20} id="ft" />
            silkvoice
          </div>
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            <Link to="/terms" style={{ fontSize: 12, color: 'var(--color-muted-ink)', textDecoration: 'none' }}>{t('landing.footer.terms')}</Link>
            <Link to="/privacy" style={{ fontSize: 12, color: 'var(--color-muted-ink)', textDecoration: 'none' }}>{t('landing.footer.privacy')}</Link>
            <Link to="/pricing" style={{ fontSize: 12, color: 'var(--color-muted-ink)', textDecoration: 'none' }}>{t('landing.footer.pricing')}</Link>
            <Link to="/blog" style={{ fontSize: 12, color: 'var(--color-muted-ink)', textDecoration: 'none' }}>{t('landing.footer.blog', 'Blog')}</Link>
            <Link to="/status" style={{ fontSize: 12, color: 'var(--color-muted-ink)', textDecoration: 'none' }}>{t('landing.footer.status', 'Status')}</Link>
          </div>
          <LanguagePicker variant="inline" />
          <div style={{ fontSize: 12, color: 'var(--color-faint)' }}>
            &copy; {new Date().getFullYear()} silkvoice
          </div>
        </div>
      </footer>

      {/* ── Styles ────────────────────────────────────────────────── */}
      <style>{`
        /* Eyebrow dot pulse */
        @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:.3} }
        .eyebrow-dot { animation: pulse 2s ease-in-out infinite; }

        /* Hero fadeIn */
        @keyframes fadeIn { from{opacity:0;transform:translateY(12px)} to{opacity:1;transform:none} }

        /* Blink dot for processing status */
        @keyframes blinkDot { 0%,100%{opacity:1} 50%{opacity:.2} }

        /* Landing buttons */
        .landing-btn {
          display: inline-flex; align-items: center; gap: 7px;
          padding: 11px 22px; border-radius: 10px;
          font-size: 14px; font-weight: 700;
          cursor: pointer; transition: all .18s; text-decoration: none;
          white-space: nowrap; border: none;
        }
        .landing-btn svg { width: 14px; height: 14px; stroke: currentColor; fill: none; stroke-width: 2; flex-shrink: 0; }
        .landing-btn-fill {
          background: linear-gradient(135deg, #FF006E, #FFBE0B); color: #fff;
          box-shadow: 0 1px 3px rgba(255,0,110,.18), 0 4px 14px rgba(255,0,110,.14);
        }
        .landing-btn-fill svg { stroke: rgba(255,255,255,.8); }
        .landing-btn-fill:hover {
          background: linear-gradient(135deg, #D4005C, #E5AA00); transform: translateY(-1px);
          box-shadow: 0 3px 8px rgba(255,0,110,.22), 0 8px 22px rgba(255,0,110,.16);
        }
        .landing-btn-line {
          background: rgba(255,255,255,.05); color: var(--color-text);
          border: 1.5px solid var(--color-glass-border); box-shadow: var(--shadow-sm);
        }
        .landing-btn-line:hover {
          border-color: var(--color-faint); background: rgba(255,255,255,.09);
          box-shadow: var(--shadow-md);
        }

        /* Gallery card hover */
        .gcard:hover { transform: translateY(-3px); box-shadow: var(--shadow-md); border-color: var(--color-faint) !important; }
        .gcard:hover .gthumb-play { opacity: 1 !important; transform: translate(-50%,-50%) scale(1.05) !important; }

        /* Step card hover */
        .step-card:hover { background: rgba(255,255,255,0.03) !important; }

        /* Pricing toggle */
        .pricing-toggle {
          display: inline-flex;
          border: 1px solid var(--color-glass-border);
          border-radius: 10px;
          overflow: hidden;
          background: var(--color-surface);
        }
        .pricing-toggle-btn {
          padding: 8px 20px;
          font-size: 13px;
          font-weight: 600;
          border: none;
          background: transparent;
          color: var(--color-text-muted);
          cursor: pointer;
          transition: all 0.18s;
          font-family: inherit;
        }
        .pricing-toggle-btn.active {
          background: linear-gradient(135deg, #FF006E, #FFBE0B);
          color: #fff;
        }

        /* Pricing card hover */
        .pcard:hover { transform: translateY(-2px); box-shadow: var(--shadow-md); }
        .pcard-btn:hover { border-color: var(--color-faint) !important; background: rgba(255,255,255,.07) !important; }
        .pcard-btn.accent:hover { background: linear-gradient(135deg, #D4005C, #E5AA00) !important; border-color: transparent !important; }

        /* Compliance card hover */
        .cgcard:hover { box-shadow: var(--shadow-sm); }

        /* Hero card thumb hover play button */
        .ct-thumb:hover .ct-play { opacity: 1 !important; }

        /* Marquee row edge fade */
        .lang-row { overflow: hidden; mask-image: linear-gradient(90deg,transparent,black 6%,black 94%,transparent); }

        /* Footer link hover */
        .footer-inner a:hover { color: var(--color-text-muted) !important; }

        /* ── Responsive ── */
        @media (max-width: 860px) {
          .hero-section { grid-template-columns: 1fr !important; gap: 44px !important; padding: 100px 24px 60px !important; }
          .hero-card-wrap { display: none !important; }
          .proof-inner { grid-template-columns: 1fr 1fr !important; padding: 0 24px !important; }
          .proof-item { border-left: none !important; }
          .proof-item:nth-child(2), .proof-item:nth-child(4) { border-left: 1px solid var(--color-glass-border) !important; }
          .steps { grid-template-columns: 1fr 1fr !important; }
          .gallery-grid { grid-template-columns: 1fr 1fr !important; }
          .gcard.big { grid-column: auto !important; }
          .ext-grid { grid-template-columns: 1fr !important; }
          .compliance-grid { grid-template-columns: 1fr !important; }
          .cta-section { padding: 48px 24px !important; }
          .cta-inner { padding: 44px 28px !important; }
          .footer-inner { padding: 0 24px !important; }
        }
        @media (max-width: 560px) {
          .steps { grid-template-columns: 1fr !important; }
          .gallery-grid { grid-template-columns: 1fr !important; }
          .ext-mockup { display: none !important; }
          .hero-ctas { flex-direction: column !important; }
          .landing-btn { width: 100%; justify-content: center; }
          h1 { font-size: 36px !important; }
        }
      `}</style>
    </div>
  )
}

/* ── Sub-components ───────────────────────────────────────────────────────── */

function HeroCard() {
  const { t } = useTranslation()
  return (
    <div style={{
      background: 'var(--color-surface)',
      border: '1px solid var(--color-glass-border)',
      borderRadius: 'var(--radius-lg)',
      boxShadow: 'var(--shadow-lg)',
      overflow: 'hidden', position: 'relative',
    }}>
      {/* Silk gradient top edge */}
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, background: 'linear-gradient(135deg,#FFBE0B,#FF006E,#00F5D4,#3A0CA3)', opacity: 0.7, zIndex: 1 }} />

      {/* Browser chrome */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8, padding: '11px 14px',
        background: 'rgba(255,255,255,0.03)', borderBottom: '1px solid var(--color-glass-border)',
      }}>
        <div style={{ display: 'flex', gap: 5 }}>
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#FF5F57' }} />
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#FEBC2E' }} />
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#28C840' }} />
        </div>
        <div style={{
          flex: 1, maxWidth: 220, margin: '0 auto', height: 20,
          background: 'rgba(255,255,255,0.06)', border: '1px solid var(--color-glass-border)',
          borderRadius: 5, display: 'flex', alignItems: 'center', padding: '0 8px', gap: 5,
        }}>
          <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#4CAF50', flexShrink: 0 }} />
          <span style={{ fontSize: 10, color: 'var(--color-muted-ink)' }}>silkvoice.io</span>
        </div>
      </div>

      {/* Body */}
      <div style={{ padding: 16 }}>
        {/* URL row */}
        <div style={{ display: 'flex', gap: 7, marginBottom: 12 }}>
          <div style={{
            flex: 1, height: 34, border: '1.5px solid rgba(255,90,60,0.18)',
            borderRadius: 7, background: 'rgba(255,90,60,0.06)',
            display: 'flex', alignItems: 'center', gap: 7, padding: '0 11px',
            boxShadow: '0 0 0 3px rgba(255,90,60,0.04)',
          }}>
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="var(--color-muted-ink)" strokeWidth="2">
              <path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71" />
              <path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71" />
            </svg>
            <span style={{ fontSize: 11, color: 'var(--color-muted-ink)', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
              youtube.com/watch?v=dQw4w9WgXcQ
            </span>
          </div>
          <button style={{
            height: 34, padding: '0 14px', borderRadius: 7,
            background: 'linear-gradient(135deg,#FF006E,#FFBE0B)',
            border: 'none', color: '#fff', fontSize: 11, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap',
          }}>
            {t('landing.heroCard.dubIt')}
          </button>
        </div>

        {/* Language row */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 9, marginBottom: 16,
          paddingBottom: 14, borderBottom: '1px solid var(--color-glass-border)',
        }}>
          <span style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: 'var(--color-muted-ink)' }}>
            {t('landing.heroCard.dubInto')}
          </span>
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: 5,
            padding: '4px 10px', borderRadius: 6, background: 'rgba(255,255,255,0.06)',
            border: '1px solid var(--color-glass-border)', fontSize: 11, color: 'var(--color-text)', fontWeight: 600,
          }}>
            {'\u{1F1F0}\u{1F1F7}'} {t('languages.ko')} &#9662;
          </div>
          <span style={{ marginLeft: 'auto', fontSize: 10, color: 'var(--color-muted-ink)' }}>
            {t('landing.heroCard.sourceAutoDetect')}
          </span>
        </div>

        {/* Dubbed videos label */}
        <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: 'var(--color-muted-ink)', marginBottom: 8 }}>
          {t('landing.heroCard.yourDubs')}
        </div>

        {/* Thumbnail grid — 2x2 */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
          {HERO_THUMBS.map((thumb, i) => (
            <div key={i} className="ct-thumb" style={{
              borderRadius: 7, overflow: 'hidden', aspectRatio: '16/9',
              position: 'relative', cursor: 'pointer', border: '1px solid var(--color-glass-border)',
            }}>
              <img src={thumb.img} alt={thumb.title} loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
              <div style={{
                position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column',
                alignItems: 'flex-start', justifyContent: 'flex-end', padding: 8,
                background: 'linear-gradient(transparent 30%, rgba(0,0,0,0.55) 100%)',
              }}>
                <span style={{ fontSize: 8, color: 'rgba(255,255,255,0.5)', fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase', marginBottom: 2 }}>
                  {thumb.channel}
                </span>
                <span style={{ fontSize: 9, color: 'rgba(255,255,255,0.85)', fontWeight: 700, lineHeight: 1.3 }}>
                  {thumb.title}
                </span>
              </div>
              {/* Language badge */}
              <div style={{
                position: 'absolute', top: 5, right: 5, padding: '2px 5px', borderRadius: 3,
                background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)',
                fontSize: 8, fontWeight: 700, color: 'rgba(255,255,255,0.7)',
              }}>
                {thumb.lang}
              </div>
              {/* Play button on hover */}
              <div className="ct-play" style={{
                position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-52%)',
                width: 26, height: 26, borderRadius: '50%',
                background: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(6px)',
                border: '1px solid rgba(255,255,255,0.2)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                opacity: 0, transition: 'opacity 0.2s',
              }}>
                <svg width="9" height="9" viewBox="0 0 24 24" fill="#fff" stroke="none" style={{ marginLeft: 2 }}>
                  <polygon points="8 5 19 12 8 19" />
                </svg>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

function LanguageMarquee({ row, speed, reverse, t }: { row: typeof LANGUAGES_ROW1; speed: number; reverse: boolean; t: (key: string) => string }) {
  const items = [...row, ...row]
  return (
    <div className="lang-row">
      <div
        className="marquee-track"
        style={{
          animation: `marquee ${speed}s linear infinite${reverse ? ' reverse' : ''}`,
        }}
      >
        {items.map(({ code, flag }, i) => (
          <span
            key={`${code}-${i}`}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '6px 13px', borderRadius: 20,
              background: 'var(--color-surface)',
              border: '1px solid var(--color-glass-border)',
              fontSize: 12, fontWeight: 500, color: 'var(--color-text-muted)',
              whiteSpace: 'nowrap', flexShrink: 0,
            }}
          >
            <span style={{ fontSize: 14 }}>{flag}</span>
            <span>{t(`languages.${code}`)}</span>
          </span>
        ))}
      </div>
    </div>
  )
}

/* ── SVG Icons ────────────────────────────────────────────────────────────── */

function SilkVoiceLogo({ size = 20, id = 'lp' }: { size?: number; id?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 1000 1000" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient id={`${id}-g1`} x1="300" y1="250" x2="300" y2="750" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor="#FFBE0B" />
          <stop offset="50%" stopColor="#FF006E" />
          <stop offset="100%" stopColor="#00F5D4" />
        </linearGradient>
        <linearGradient id={`${id}-g2`} x1="300" y1="250" x2="800" y2="500" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor="#FFBE0B" />
          <stop offset="100%" stopColor="#3A0CA3" />
        </linearGradient>
        <linearGradient id={`${id}-g3`} x1="300" y1="750" x2="800" y2="500" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor="#00F5D4" />
          <stop offset="100%" stopColor="#3A0CA3" />
        </linearGradient>
      </defs>
      <path d="M300 250 C150 400,150 600,300 750 C400 650,500 600,500 500 C500 400,400 350,300 250Z" fill={`url(#${id}-g1)`} opacity=".9" />
      <path d="M300 750 C500 850,700 680,800 500 C680 480,550 500,440 500 C440 580,400 650,300 750Z" fill={`url(#${id}-g3)`} />
      <path d="M300 250 C500 150,700 320,800 500 C680 520,550 500,440 500 C440 420,400 350,300 250Z" fill={`url(#${id}-g2)`} />
    </svg>
  )
}
